/*

createStore ：对接口函数进行封装，实现缓存控制

example:
//比如一个获取当前登陆用户信息的接口
getCurrentUser().then((res)=> console.log(res) )


//接口通过 createStore 封装之后，会在第一次请求完成之后对结果进行缓存，第二次请求的时候则直接从缓存中取数据
getCurrentUserWithCache = createStore(getCurrentUser)
getCurrentUserWithCache().then((res)=> console.log(res))


//封装之后的 函数会同时具有以下方法
updateCache ： 根据给定的数据更改缓存
hasCache ： 判断当前是否有缓存
clearCache ： 清空缓存
onClearCache ： 清空缓存事件
onUpdateCache ： 当缓存被更改后的事件

// example
getUserWithCache.clearCache()
getUserWithCache.updateCache({...user, name : 'newname'})



//根据用户id获取用户信息的接口， 带有参数的接口函数处理
getUser('1').then((res)=> console.log(res) )
getUserWithCache = createStore(getUser.bind(null,'1'))
getUserWithCache().then((res)=> console.log(res))


// 进阶例子：
createStore 和 memory 进行组合，可以实现对带有参数接口的缓存,memory 的使用参考  memory.ts

const cacheRemoterfactory = memory((id : string)=> createStore(getUser.bind(null,id))  )
const getUser1 = cacheRemoterfactory('1')
const getUser2 = cacheRemoterfactory('2')

getUser1().then((res)=> console.log('user1 data') )
getUser2().then((res)=> console.log('user2 data') )

 */
import EventBus, { IHandler } from '../EventBus'

const Events = {
	ClearCache : 'clearCache',
	UpdateCache : 'updateCache'
} 


export interface IStoreRemoter<U extends any[],T>{
	(...args : U) : Promise<T>
	updateCache : (updates : Promise<T> | T)=> void
	hasCache : ()=> boolean
	clearCache : ()=> void
	onClearCache : (handler : IHandler<any>)=> ()=>void
	onUpdateCache : (handler : IHandler<any>)=> ()=>void
}

export interface ICreateStore {
	<U extends any[],T>(remoter : (...args: U) => Promise<T> ) : IStoreRemoter<U,T>
	<U extends any[],T,R>(remoter : (...args: U) => Promise<T>,filter? : (data : T)=> R | PromiseLike<R>) : IStoreRemoter<U,R>
}

const createStore = (<U extends any[],T,R>(remoter :  (...args: U) => Promise<T>, filter? : (data : T)=> R | PromiseLike<R>)=>{
	let result : Promise<T | R> | null
	if(remoter){

		const eventBus = new EventBus()
		const storeRemoter = ((...args : U)=> {
			if(result){
				return result
			}else{
				result = remoter.apply(null,args)
					.then<T | R>(res => typeof filter === 'function' ? filter(res) : res)
					.catch((error : Response | T) => {
						throw error
					})
			}
			return result
		}) as IStoreRemoter<U,T>

		storeRemoter.updateCache = (updates : Promise<T | R> | (T | R) )=>{
			result = Promise.resolve(updates)
			eventBus.trigger(Events.UpdateCache)
		}

		storeRemoter.hasCache = ()=>{
			return result != null
		}
		
		storeRemoter.clearCache = ()=>{
			result = null
			eventBus.trigger(Events.ClearCache)
		}

		storeRemoter.onUpdateCache = (handler)=>  eventBus.on(Events.UpdateCache,handler) 
		storeRemoter.onClearCache = (handler)=>  eventBus.on(Events.ClearCache,handler) 

		return storeRemoter
	}
}) as ICreateStore

export default createStore

export interface ICreateProxy {
	<U extends any[],T>(remoter : (...args: U) => Promise<T>) : (...args: U) => Promise<T> 
	<U extends any[],T,R>(remoter : (...args: U) => Promise<T>,filter : (data : T)=> R | PromiseLike<R>) : (...args: U) => Promise<R> 
}

export const createProxy : ICreateProxy = <U extends any[],T,R>(remoter : (...args: U) => Promise<T>, filter? : (data : T)=> R)=>{
	return ((...args : U)=> {
		return remoter.apply(null,args)
			.then(res => filter ? filter(res) : res)
			.catch((error : Response | T) => {
				throw error
			})
	})
}